import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { IGetInfiniteScrollRows } from 'rev-shared/ui/dataGrid/infiniteScroll/IGetInfiniteScrollRows';

import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { formatMediumDateTime } from 'rev-shared/date/DateFormatters';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';

import { AnalyticsService } from '../Analytics.Service';
import { IEventAnalyticsKpis } from './IEventAnalyticsKpis';
import { MAX_LIVE_EVENTS_COUNT } from '../Constant';

@Injectable()
export class EventAnalyticsService {

	private readonly liveEventsContextSubject$: BehaviorSubject<any> = new BehaviorSubject<any>({});
	public liveEventsContext$: Observable<any> = this.liveEventsContextSubject$.asObservable();

	constructor(
		private readonly AnalyticsService: AnalyticsService,
		private readonly DateParsers: DateParsersService,
		private readonly http: HttpClient
	) {}

	public getLiveEventRows: IGetInfiniteScrollRows = params => {
		params.pageSize = MAX_LIVE_EVENTS_COUNT;
		return this.AnalyticsService.getRows(params)
			.then(result => {
				this.liveEventsContextSubject$.next({
					totalCount: result.totalRows, //fix once backend provide data
					displayCount: result.data.length,
					searchQuery: params.query
				});
				result.data = result.data?.map(item => ({
					id: item.id,
					startDate: this.DateParsers.parseUTCDate(item.startDate),
					status: item.status,
					title: item.title,
					attendeesCount: item.performance.attendeesCount,
					avgRebufferingDurationInSeconds: item.performance.avgRebufferingDurationInSeconds,
					experiencedErrors: item.performance.experiencedErrors,
					rebufferingPerc: item.performance.rebufferingPerc,
					canEdit: item.canEdit
				}));

				//dummy row push to show more record message.
				if (result.totalRows > MAX_LIVE_EVENTS_COUNT) {
					result.data.push({ fullWidth: true });
				}

				//totalRows === data.length to stop infinite scroll.
				result.totalRows = result.data.length;
				return result;
			});
	};

	public getEventActivityKpis(accountId: string, params: any): Observable<IEventAnalyticsKpis> {
		return this.http.get<any>(`/reports/account/${accountId}/events/kpi`, { params })
			.pipe(
				map(data => ({
					avgDurationSecs: data.durationAverageSeconds,
					totalDurationSecs: data.durationTotalSeconds,
					totalEvents: data.count,
					uniqueAttendees: data.uniqueAttendeesCount
				}))
			);
	}

	public getAttendeesByEvent(accountId: string, params: any): Observable<any> {
		return this.http.get<any>(`/reports/account/${accountId}/events/attendees`, { params })
			.pipe(
				map(result => result.buckets)
			);
	}

	public initiateEventsDownload(accountId: string, fetchContext: { startDate: string, endDate: string }): Promise<void> {
		return lastValueFrom(this.http.get<any>(`/reports/account/${accountId}/events/csv`, {
			params: {
				startDate: fetchContext.startDate,
				endDate: fetchContext.endDate
			}
		}));
	}
}
